import { RootState } from '../../store.ts'
import { createSelector } from '@reduxjs/toolkit'
import {
  Category,
  Product,
  SortBy,
  Species,
} from '@/data/replenishmentPlan/slice/replenishmentPlan.types.ts'
import { getProductsCostForNext3Months } from '@/core/utils/getProductCostForNext3Months.ts'

export const selectDisplayValueMode = (state: RootState) =>
  state.replenishmentPlan.planTableFilter.displayValueMode

export const selectDisplayProductsMode = (state: RootState) =>
  state.replenishmentPlan.planTableFilter.displayProductsMode

export const selectSort = (state: RootState) =>
  state.replenishmentPlan.planTableFilter.sort

export const selectSelectedSpecies = (state: RootState) =>
  state.replenishmentPlan.planTableFilter.selectedSpecies

export const selectSearch = (state: RootState) =>
  state.replenishmentPlan.planTableFilter.search

// Products in plan
export const selectPlanProducts = (state: RootState) =>
  state.replenishmentPlan.planProducts

// Products from search (may include products already in the plan; `isInPlan` is derived where used)
export const selectProducts = (state: RootState) =>
  state.replenishmentPlan.products

export const selectForecastItems = (state: RootState) =>
  state.replenishmentPlan.forecastItems

export const selectPlannedItems = (state: RootState) =>
  state.replenishmentPlan.plannedItems

export const selectShipmentItems = (state: RootState) =>
  state.replenishmentPlan.shipmentItems

export const selectSubmittedItems = (state: RootState) =>
  state.replenishmentPlan.submittedItems

export const UNAVAILABLE_CATEGORY_ID = -999
export const UNAVAILABLE_CATEGORY: Category = {
  id: UNAVAILABLE_CATEGORY_ID,
  name: 'Unavailable',
  code: 'UNAVAILABLE',
}

export const selectCategories = createSelector(
  selectPlanProducts,
  (planProducts) => {
    const categories: Category[] = []
    let hasUnavailableProducts = false

    for (const product of planProducts) {
      if (product.unavailable) {
        hasUnavailableProducts = true
      }

      if (!product.category) {
        continue
      }

      const categoryExists = Array.from(categories).some(
        (c) => c.id === product.category?.id,
      )

      if (!categoryExists) {
        categories.push(product.category)
      }
    }

    if (hasUnavailableProducts) {
      return [UNAVAILABLE_CATEGORY, ...Array.from(categories)]
    }

    return Array.from(categories)
  },
)

// The classified species list loaded from the `planSpecies` query.
export const selectSpeciesList = (state: RootState) =>
  state.replenishmentPlan.species

// The species list grouped into the two filter tabs (Pest / Animal), sorted by name.
export const selectSpecies = createSelector(selectSpeciesList, (species) => {
  const byName = (a: Species, b: Species) => a.name.localeCompare(b.name)

  return {
    pest: species.filter((item) => item.group === 'pest').sort(byName),
    animal: species.filter((item) => item.group === 'animal').sort(byName),
  }
})

export const selectFilteredSortedPlanProducts = createSelector(
  selectSort,
  selectPlannedItems,
  selectSelectedSpecies,
  selectSearch,
  selectPlanProducts,
  (sort, plannedItems, selectedSpecies, search, planProducts) => {
    const lowerCaseSearch = search.toLowerCase()

    const selectedSanaIds = new Set(
      selectedSpecies.map((species) => species.sanaId),
    )

    const filteredSortedPlanProducts = planProducts
      .filter((product) => {
        const matchesSearch = [product.name, product.code].some((str) =>
          str.toLowerCase().includes(lowerCaseSearch),
        )

        const matchesSpecies =
          selectedSanaIds.size === 0 ||
          product.speciesIds.some((id) => selectedSanaIds.has(id))

        return matchesSearch && matchesSpecies
      })
      .sort((a, b) => {
        // Unavailable products always on top
        if (a.unavailable && !b.unavailable) return -1
        if (!a.unavailable && b.unavailable) return 1

        if (sort.sortBy === SortBy.alphabetically) {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        } else if (sort.sortBy === SortBy.cost) {
          const aProductCost = getProductsCostForNext3Months([a], plannedItems)
          const bProductCost = getProductsCostForNext3Months([b], plannedItems)

          return aProductCost - bProductCost
        } else {
          return a.price - b.price
        }
      })

    if (!sort.asc) {
      // Keep unavailable at top even when reversed
      const unavailableProducts = filteredSortedPlanProducts.filter(
        (p) => p.unavailable,
      )
      const availableProducts = filteredSortedPlanProducts.filter(
        (p) => !p.unavailable,
      )
      availableProducts.reverse()
      return [...unavailableProducts, ...availableProducts]
    }

    return filteredSortedPlanProducts
  },
)

const UNAVAILABLE_GROUP_KEY = 'UNAVAILABLE--UNAVAILABLE'

// Plan-browse selector, grouped (no search filtering — search is server-driven via selectTableProducts).
export const selectFilteredSortedPlanGroupedProducts = createSelector(
  selectSort,
  selectPlannedItems,
  selectSelectedSpecies,
  selectSearch,
  selectPlanProducts,
  (sort, plannedItems, selectedSpecies, search, planProducts) => {
    const lowerCaseSearch = search.toLowerCase()

    const selectedSanaIds = new Set(
      selectedSpecies.map((species) => species.sanaId),
    )

    const filteredPlanProducts = planProducts.filter((product) => {
      const matchesSearch = [product.name, product.code].some((str) =>
        str.toLowerCase().includes(lowerCaseSearch),
      )

      const matchesSpecies =
        selectedSanaIds.size === 0 ||
        product.speciesIds.some((id) => selectedSanaIds.has(id))

      return matchesSearch && matchesSpecies
    })

    const productsByKey: Record<string, Product[]> = {}

    for (const product of filteredPlanProducts) {
      let key: string

      if (product.unavailable) {
        key = UNAVAILABLE_GROUP_KEY
      } else {
        const groupId = product.group?.name ?? ''
        const categoryId = product.category?.name ?? ''
        key = [groupId, categoryId].join('-')
      }

      if (!productsByKey[key]) {
        productsByKey[key] = []
      }

      productsByKey[key].push(product)
    }

    const keys = Object.keys(productsByKey)

    const sortedKeys = keys.sort((a, b) => {
      if (a === UNAVAILABLE_GROUP_KEY) return -1
      if (b === UNAVAILABLE_GROUP_KEY) return 1

      const [aGroup, aCategory] = a.split('-').map((s) => s.toLowerCase())
      const [bGroup, bCategory] = b.split('-').map((s) => s.toLowerCase())

      if (sort.sortBy === SortBy.alphabetically) {
        if (aCategory && !bCategory) {
          return -1
        }

        if (!aCategory && bCategory) {
          return 1
        }

        if (aCategory === bCategory) {
          return aGroup.localeCompare(bGroup)
        }

        return aCategory.localeCompare(bCategory)
      } else if (sort.sortBy === SortBy.cost) {
        const aProducts = productsByKey[a]
        const bProducts = productsByKey[b]

        const aProductsCost = getProductsCostForNext3Months(
          aProducts,
          plannedItems,
        )
        const bProductsCost = getProductsCostForNext3Months(
          bProducts,
          plannedItems,
        )

        return aProductsCost - bProductsCost
      } else {
        const aProducts = productsByKey[a]
        const bProducts = productsByKey[b]

        const mostExpensiveA = aProducts.reduce(
          (acc, product) => (product.price > acc ? product.price : acc),
          0,
        )
        const mostExpensiveB = bProducts.reduce(
          (acc, product) => (product.price > acc ? product.price : acc),
          0,
        )

        return mostExpensiveA - mostExpensiveB
      }
    })

    if (!sort.asc) {
      const unavailableIndex = sortedKeys.indexOf(UNAVAILABLE_GROUP_KEY)
      if (unavailableIndex > -1) {
        sortedKeys.splice(unavailableIndex, 1)
        sortedKeys.reverse()
        sortedKeys.unshift(UNAVAILABLE_GROUP_KEY)
      } else {
        sortedKeys.reverse()
      }
    }

    const result: Product[] = []

    for (const key of sortedKeys) {
      const productsGroup = productsByKey[key]

      productsGroup.sort((a, b) => {
        if (sort.sortBy === SortBy.alphabetically) {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
        } else if (sort.sortBy === SortBy.cost) {
          const aProductCost = getProductsCostForNext3Months([a], plannedItems)
          const bProductCost = getProductsCostForNext3Months([b], plannedItems)

          return aProductCost - bProductCost
        } else {
          return a.price - b.price
        }
      })

      if (!sort.asc) {
        productsGroup.reverse()
      }

      result.push(...productsGroup)
    }

    return result
  },
)

export const selectIsSearchingProducts = (state: RootState) =>
  state.replenishmentPlan.isSearchingProducts

export const selectIsLoadingPlan = (state: RootState) =>
  state.replenishmentPlan.isLoadingPlan

export const selectReplaceProductId = (state: RootState) =>
  state.replenishmentPlan.replaceProductId

// While a term is active, the plain-list table is driven entirely by the server search results
// (tokenized + ranked, and including in-plan products). `planProducts` membership is the single
// source of truth for whether a product is in the plan: the server result omits both `isInPlan`
// and `salableProductId`, and `salableProductId` is what the remove action needs.
// When there is no term, it falls back to the user-sorted plan products.
export const selectTableProducts = createSelector(
  selectSearch,
  selectFilteredSortedPlanProducts,
  selectProducts,
  selectPlanProducts,
  (search, filteredSortedPlanProducts, products, planProducts) => {
    if (!search) {
      return filteredSortedPlanProducts
    }

    const planProductsById = new Map(
      planProducts.map((planProduct) => [planProduct.id, planProduct]),
    )

    return products.map((product) => {
      const planProduct = planProductsById.get(product.id)

      return planProduct
        ? {
            ...product,
            isInPlan: true,
            salableProductId: planProduct.salableProductId,
          }
        : { ...product, isInPlan: false, salableProductId: null }
    })
  },
)

export const selectReplaceProduct = createSelector(
  selectReplaceProductId,
  selectTableProducts,
  (replaceProductId, tableProducts) => {
    return (
      tableProducts.find((product) => product.id === replaceProductId) ?? null
    )
  },
)
